import { atom, onMount } from "nanostores";
import z from "zod";
import type { PluginEvent, PluginEventsDefinition } from "@/plugins/server/types";
import type { Message } from "@/shared/messages";
import { definePluginClient } from "../../client/builder";
import type { generationPlugin } from "./server";
import type { generationPluginEventsDef } from "./server/events";
import type { GenerationStatus } from "./server/status";

type PluginEventDataInput<
  EventsDef extends PluginEventsDefinition,
  Name extends EventsDef[number]["name"],
> = Extract<PluginEvent<EventsDef, "input">, { name: Name }> extends { data: infer Data }
  ? Data
  : never;

export const generationPluginClient = definePluginClient<typeof generationPlugin>("generation")
  .config({ schema: z.object({ enableVoice: z.boolean().prefault(false) }) })
  .class(
    ({ plugin }) =>
      class {
        async continue(
          params: PluginEventDataInput<typeof generationPluginEventsDef, "agent.continue">,
        ) {
          return await plugin.server.events.emit({ name: "agent.continue", data: params });
        }
        async decide(
          params: PluginEventDataInput<typeof generationPluginEventsDef, "agent.decide">,
        ) {
          return await plugin.server.events.emit({ name: "agent.decide", data: params });
        }
        async say(params: PluginEventDataInput<typeof generationPluginEventsDef, "agent.say">) {
          return await plugin.server.events.emit({ name: "agent.say", data: params });
        }
        async interrupt(
          params: PluginEventDataInput<typeof generationPluginEventsDef, "agent.interrupt">,
        ) {
          return await plugin.server.events.emit({ name: "agent.interrupt", data: params });
        }
        messages = {
          create: async (
            params: PluginEventDataInput<typeof generationPluginEventsDef, "messages.create">,
          ) => await plugin.server.events.emit({ name: "messages.create", data: params }),
          update: async (
            params: PluginEventDataInput<typeof generationPluginEventsDef, "messages.update">,
          ) => await plugin.server.events.emit({ name: "messages.update", data: params }),
          get: () => plugin.server.context.get().messages,
        };
      },
  )
  .atoms(({ plugin, telemetry }) => [
    {
      name: "status",
      create: () => {
        const store = atom<GenerationStatus | null>(null);

        // Subscribe to status changes
        onMount(store, () => {
          // Fetch initial status from context
          const [error, context] = plugin.server.context.safe.get();
          if (error)
            telemetry.log.error({
              message: "Failed to fetch initial status from context.",
              error,
            });
          if (context?.status) store.set(context.status);

          // Subscribe to status changes from the context
          const unsubscribe = plugin.server.context.onChange(
            (ctx) => ctx.status,
            (ctx) => store.set(ctx.status),
          );

          // Return cleanup function
          return () => unsubscribe?.();
        });

        return { store, refresh: async () => void 0 };
      },
    },
    {
      name: "messages",
      create: () => {
        const store = atom<Message[]>([]);
        // Subscribe to messages changes
        onMount(store, () => {
          // Fetch initial messages from context (if availabe, i.e, agent started)
          const [error, context] = plugin.server.context.safe.get();
          if (error)
            telemetry.log.error({
              message: "Failed to fetch initial messages from context.",
              error,
            });

          if (context?.messages) store.set(context.messages);

          // Subscribe to messages changes from the context
          const unsubscribe = plugin.server.context.onChange(
            (ctx) => ctx.messages,
            (ctx) => store.set(ctx.messages),
          );

          // Return cleanup function
          return () => unsubscribe?.();
        });

        return { store, refresh: async () => void 0 };
      },
    },
  ]);
